import type { CommandProps } from '@tiptap/core';
import { TableCell } from '@tiptap/extension-table';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { TableMap } from '@tiptap/pm/tables';
import { formatNumberBR, parseBRNumber } from '../formatos';

/**
 * FÓRMULAS NA TABELA — as do Word, em português.
 *
 *   =SOMA(ACIMA)  =SOMA(ESQUERDA)  =MÉDIA(ACIMA)  =MÁXIMO(ACIMA)
 *   =MÍNIMO(ACIMA)  =CONTAR(ACIMA)  e contas livres: =B2-C2, =D5*1,1
 *
 * Colunas são letras e linhas são números, contando a linha de cabeçalho
 * como 1 — a mesma convenção do Word. A célula guarda a fórmula em
 * `data-formula`; o texto dela é o resultado, refeito a cada mudança na
 * tabela. Assim o HTML exportado traz o número já calculado, e quem abre
 * no Word vê o valor.
 *
 * Números em pt-BR: "18.240,00" é dezoito mil duzentos e quarenta.
 */

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    formulas: {
      /** Põe uma fórmula na célula do cursor. Texto sem "=" vira valor comum. */
      definirFormula: (formula: string) => ReturnType;
    };
  }
}

export const CelulaComFormula = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      formula: {
        default: null as string | null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-formula'),
        renderHTML: (attrs: { formula: string | null }) =>
          attrs.formula ? { 'data-formula': attrs.formula, style: 'text-align:right;font-variant-numeric:tabular-nums' } : {},
      },
      alinhamento: {
        default: null as string | null,
        parseHTML: (el: HTMLElement) => el.style.textAlign || null,
        renderHTML: (attrs: { alinhamento: string | null }) => (attrs.alinhamento ? { style: `text-align:${attrs.alinhamento}` } : {}),
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      definirFormula:
        (formula: string) =>
        ({ state, tr, dispatch }: CommandProps) => {
          const cel = celulaNoCursor(state);
          if (!cel) return false;
          const valor = formula.trim();
          if (valor.startsWith('=')) tr.setNodeMarkup(cel.pos, undefined, { ...cel.node.attrs, formula: valor });
          else {
            tr.setNodeMarkup(cel.pos, undefined, { ...cel.node.attrs, formula: null });
            substituirTexto(tr, cel.pos, cel.node, valor, state);
          }
          if (dispatch) dispatch(tr);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [...(this.parent?.() ?? []), pluginRecalculo()];
  },
});

export function celulaNoCursor(state: EditorState): { node: PMNode; pos: number } | null {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'tableCell' || n.type.name === 'tableHeader') return { node: n, pos: $from.before(d) };
  }
  return null;
}

/** A referência "B3" da célula do cursor, para a barra de fórmula. */
export function referenciaNoCursor(state: EditorState): string | null {
  const cel = celulaNoCursor(state);
  if (!cel) return null;
  const $c = state.doc.resolve(cel.pos);
  for (let d = $c.depth; d >= 0; d--) {
    const tabela = $c.node(d);
    if (tabela.type.name !== 'table') continue;
    const inicio = $c.start(d);
    const mapa = TableMap.get(tabela);
    const r = mapa.findCell(cel.pos - inicio);
    return letra(r.left) + (r.top + 1);
  }
  return null;
}

const letra = (i: number) => String.fromCharCode(65 + i);

/** Valor numérico do texto de uma célula, pelo canônico do Art. 29; null se não há número. */
export function numeroPtBr(txt: string): number | null {
  return /\d/.test(txt) ? parseBRNumber(txt) : null;
}
const moeda = (v: number) => formatNumberBR(v, 2);

/** Troca o conteúdo da célula em `pos` (posição no `tr.doc` atual) por um parágrafo com `texto`. */
function substituirTexto(tr: Transaction, pos: number, celula: PMNode, texto: string, state: EditorState) {
  const inicio = pos + 1;
  const fim = inicio + celula.content.size;
  const paragrafo = state.schema.nodes.paragraph.create(celula.firstChild?.attrs ?? null, texto ? state.schema.text(texto) : null);
  tr.replaceWith(inicio, fim, paragrafo);
}

/** Avalia uma fórmula. NaN quando ela não faz sentido. */
export function avaliar(formula: string, tabela: PMNode, linha: number, coluna: number): number {
  const mapa = TableMap.get(tabela);
  const celula = (l: number, c: number): PMNode | null => {
    if (l < 0 || c < 0 || l >= mapa.height || c >= mapa.width) return null;
    return tabela.nodeAt(mapa.map[l * mapa.width + c]);
  };
  const m = /^=\s*(SOMA|M[ÉE]DIA|M[ÁA]XIMO|M[ÍI]NIMO|CONTAR)\s*\(\s*(ACIMA|ESQUERDA)\s*\)\s*$/i.exec(formula);
  if (m) {
    const vals: number[] = [];
    if (/^ACIMA$/i.test(m[2])) {
      for (let l = linha - 1; l >= 0; l--) {
        const c = celula(l, coluna);
        if (!c || c.type.name === 'tableHeader') continue;
        const v = numeroPtBr(c.textContent);
        if (v !== null) vals.push(v);
      }
    } else {
      for (let c = coluna - 1; c >= 0; c--) {
        const cel = celula(linha, c);
        const v = cel ? numeroPtBr(cel.textContent) : null;
        if (v !== null) vals.push(v);
      }
    }
    const fn = m[1].toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (fn === 'CONTAR') return vals.length;
    if (!vals.length) return 0;
    if (fn === 'SOMA') return vals.reduce((a, b) => a + b, 0);
    if (fn === 'MEDIA') return vals.reduce((a, b) => a + b, 0) / vals.length;
    if (fn === 'MAXIMO') return Math.max(...vals);
    return Math.min(...vals);
  }
  // Conta livre: troca as referências pelos valores e só aceita aritmética.
  const expr = formula
    .replace(/^=/, '')
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\b([A-Z])(\d+)\b/g, (_x, col: string, lin: string) => {
      const c = celula(Number(lin) - 1, col.charCodeAt(0) - 65);
      return `(${c ? numeroPtBr(c.textContent) ?? 0 : 0})`;
    });
  if (!/^[\d\s+\-*/().]+$/.test(expr)) return NaN;
  try {
    // eslint-disable-next-line no-new-func
    const v = Function(`"use strict";return (${expr})`)() as number;
    return Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}

const CHAVE = new PluginKey('recalculoFormulas');

function primeiraDesatualizada(doc: PMNode): { pos: number; cel: PMNode; texto: string } | null {
  let achada: { pos: number; cel: PMNode; texto: string } | null = null;
  doc.descendants((tabela, posTabela) => {
    if (achada) return false;
    if (tabela.type.name !== 'table') return true;
    const mapa = TableMap.get(tabela);
    const vistas = new Set<number>();
    for (const rel of mapa.map) {
      if (vistas.has(rel)) continue;
      vistas.add(rel);
      const cel = tabela.nodeAt(rel);
      const formula = cel?.attrs.formula as string | null;
      if (!cel || !formula) continue;
      const r = mapa.findCell(rel);
      const v = avaliar(formula, tabela, r.top, r.left);
      const texto = Number.isNaN(v) ? '#ERRO' : /CONTAR/i.test(formula) ? String(v) : moeda(v);
      if (cel.textContent !== texto) { achada = { pos: posTabela + 1 + rel, cel, texto }; break; }
    }
    return false;
  });
  return achada;
}

function pluginRecalculo() {
  return new Plugin({
    key: CHAVE,
    appendTransaction: (trs, _velho, novo) => {
      if (!trs.some((t) => t.docChanged) || trs.some((t) => t.getMeta(CHAVE))) return null;
      const tr = novo.tr;
      // Uma célula por vez, sempre no documento já atualizado: trocar o texto
      // de uma muda as posições das seguintes, e uma fórmula pode depender de
      // outra (o total soma as diferenças). O teto evita laço em referência
      // circular.
      for (let i = 0; i < 200; i++) {
        const alvo = primeiraDesatualizada(tr.doc);
        if (!alvo) break;
        substituirTexto(tr, alvo.pos, alvo.cel, alvo.texto, novo);
      }
      if (!tr.docChanged) return null;
      tr.setMeta(CHAVE, true);
      tr.setMeta('addToHistory', false);
      return tr;
    },
  });
}
